import {
  COUNTRY_CONFIG,
  LANGUAGE_CONFIG,
  SITE_COUNTRY_CODE,
  SITE_COUNTRY_CODE_REGEX,
  SITE_ID
} from 'Douleutaras/constants/localization';
import memoize from 'lodash/memoize';
import cookie from 'js-cookie';
import { LANGUAGE_CODES, PAYMENT_BANK } from 'Douleutaras/constants/system';
import { CURRENCY_FORMAT } from 'Douleutaras/configuration/formatters';
import { Gis } from 'Douleutaras/components/Forms/gis.class';

type MomentLocale = (language: LANGUAGE_CODES) => string;
type MomentDisplayFormat = (language: LANGUAGE_CODES) => string;

export interface Formatter {
  currency(
    val: number | string,
    // in case val equals to zero and this flag is true, the function will return "undefined" and not '€0'
    undefinedOnZero?: boolean
  ): string;
  number(val: number | string): string;
}

export interface LocaleResponse {
  siteId: number;
  countryCode: SITE_COUNTRY_CODE;
  momentTimezone: string;
  momentLocale: MomentLocale;
  momentDateFormat: MomentDisplayFormat;
  currentLanguage: LANGUAGE_CODES;
  vatRegExp: RegExp;
  paymentMethods: Array<PAYMENT_BANK>;
  paymentMethodUnattended: PAYMENT_BANK;
  formatter: Formatter;
  fbAuthClientId: string;
  localizeLegacyUrls(url: string, language: LANGUAGE_CODES): string;
}

export const useLocalization = (): LocaleResponse => {
  const countryCode = getCountryCode();
  const siteId = SITE_ID[countryCode] as unknown as number;
  const vatRegExp = COUNTRY_CONFIG.VAT_REGEXP[countryCode];
  const paymentMethods = COUNTRY_CONFIG.ONLINE_PAYMENT_BANK[countryCode];
  const paymentMethodUnattended = COUNTRY_CONFIG.ONLINE_PAYMENT_UNATTENDED[countryCode];
  const fbAuthClientId = COUNTRY_CONFIG.FACEBOOK_AUTH_CLIENT_ID[countryCode];

  return {
    siteId,
    vatRegExp,
    countryCode,
    fbAuthClientId,
    paymentMethods,
    currentLanguage: getCurrentLanguage(),
    paymentMethodUnattended,
    momentTimezone: getMomentLocalTimezone(countryCode),
    momentLocale: (language: LANGUAGE_CODES) => getMomentLocale(language),
    momentDateFormat: (language: LANGUAGE_CODES) => getMomentDateFormat(language),
    formatter: getFormatter(countryCode),
    localizeLegacyUrls: (url: string, language: LANGUAGE_CODES) => localizeLegacyUrls(url, language)
  };
};

/**
 * Map a domain hostname to country code
 */
export const domainToSiteMapping = (fullHostname: string): SITE_COUNTRY_CODE => {
  switch (true) {
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.GR)):
      return SITE_COUNTRY_CODE.GR;
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.IE)):
      return SITE_COUNTRY_CODE.IE;
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.CY)):
      return SITE_COUNTRY_CODE.CY;
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.PT)):
      return SITE_COUNTRY_CODE.PT;
    default:
      return undefined;
  }
};

/**
 * Add languageCode for urls outside react application
 * @TODO: remove when new pros profile will be available
 */

export const localizeLegacyUrls = (originalUrl: string, language: LANGUAGE_CODES) => {
  const supportedLocales = [LANGUAGE_CODES.EN, LANGUAGE_CODES.EN_PT];

  return supportedLocales.includes(language as LANGUAGE_CODES) ? `/en${originalUrl}` : originalUrl;
};

export const getDefaultLanguageCodeByCountryCode = memoize(() => {
  const countryCode = useLocalization().countryCode;

  return COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode];
});

/**
 * Extract country code from domain name of the site. Used to determine which country site to use
 */
export const getCountryCode = memoize((host?: string): SITE_COUNTRY_CODE => {
  const resolvedHost = host ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  // Application is not in production mode and SITE_COUNTRY is set explicitly
  const siteCountryCode = (
    process.env.REAL_ENV !== 'production'
    && process.env.REAL_ENV !== 'staging'
    && process.env.SITE_COUNTRY
    && typeof SITE_COUNTRY_CODE[process.env.SITE_COUNTRY] !== 'undefined')

    ? SITE_COUNTRY_CODE[process.env.SITE_COUNTRY]
    : domainToSiteMapping(resolvedHost);

  if (typeof siteCountryCode === 'undefined') {
    throw new Error(`Site country code not detected for domain ${resolvedHost}`);
  }

  return siteCountryCode;
});

/**
 * (momentjs) Get timezone for local country
 */
export const getMomentLocalTimezone = (countryCode: string) => {

  return COUNTRY_CONFIG.MOMENT.TIMEZONE[countryCode];
};

/**
 * (momentjs) Get locale for local country
 */
export const getMomentLocale = (language: LANGUAGE_CODES) => {
  return LANGUAGE_CONFIG.MOMENT.LOCALE[language];
};

/**
 * (momentjs) Get locale for local country
 */
export const getMomentDateFormat = (language: LANGUAGE_CODES) => {
  return LANGUAGE_CONFIG.MOMENT.DISPLAY_FORMAT.FULL[language];
};

export const getFormatter = (countryCode: SITE_COUNTRY_CODE): Formatter => {
  const lng = (cookie.get('languagecode') || '').toLowerCase();

  const currentLng = COUNTRY_CONFIG.LANGUAGES_SUPPORTED[countryCode].includes(lng) ? lng
    : COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode];

  const format = (val, style: string) => {
    let data = val;

    if (!val) {
      data = 0;
    }

    return new Intl.NumberFormat(currentLng, { ...CURRENCY_FORMAT, style }).format(data);
  };

  return {
    currency: (val, undefinedOnZero) => undefinedOnZero && Number(val) === 0
      ? undefined
      : format(Number(val), 'currency')
        .replace('\u00A0', ''), // remove extra space between value and currency sign
    number: val => format(Number(val), 'decimal')
  };
};

// Based on GIS, get address information
export const displayShortAddressPerCountry = (gis: Gis) => {
  const addressComponents = gis?.address.address_components || gis?.address['addressComponents'];
  const addressLocality = addressComponents?.find(addressComponent =>
    addressComponent.types.includes('locality') && addressComponent.types.includes('political'));
  const addressSublocalityNeighborhood = addressComponents?.find(addressComponent =>
    (addressComponent.types.includes('sublocality') || addressComponent.types.includes('neighborhood'))
    && addressComponent.types.includes('political'));

  return {
    [SITE_COUNTRY_CODE.GR]: addressLocality?.long_name || addressLocality?.longName, // ie: Cholargos
    [SITE_COUNTRY_CODE.CY]: gis?.address?.[0]?.full_name || gis?.address?.[0]?.fullName,
    [SITE_COUNTRY_CODE.IE]: addressSublocalityNeighborhood?.long_name || addressSublocalityNeighborhood?.longName,
    [SITE_COUNTRY_CODE.PT]: addressSublocalityNeighborhood?.long_name || addressSublocalityNeighborhood?.longName
  };
};

// Based on GIS, get address information
export const displayFullAddressPerCountry = (gis: Gis) => {
  const formattedAddress = gis?.address?.['formattedAddress'] || gis?.address?.formatted_address;

  return {
    [SITE_COUNTRY_CODE.GR]: formattedAddress,
    [SITE_COUNTRY_CODE.CY]: gis?.address?.[0]?.full_name || gis?.address?.[0]?.fullName,
    [SITE_COUNTRY_CODE.IE]: formattedAddress,
    [SITE_COUNTRY_CODE.PT]: formattedAddress
  };
};

export const getCurrentLanguage = (): LANGUAGE_CODES => {
  const langCode = cookie.get('languagecode');
  const lowerLanguageCode = langCode?.toLowerCase();
  const countryCode = getCountryCode();

  return COUNTRY_CONFIG.LANGUAGES_SUPPORTED[countryCode].includes(lowerLanguageCode) ? lowerLanguageCode
    : COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode];
};
